'use client';

import React from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const stats = [
{ value: '10+', label: 'Years in Business', sublabel: 'Serving Uganda & East Africa', color: 'red' },
{ value: '8+', label: 'Major Brands', sublabel: 'Mercedes, MAN, Scania, Volvo & more', color: 'blue' },
{ value: '7,000+', label: 'Parts in Stock', sublabel: 'Ready for immediate dispatch', color: 'yellow' },
{ value: '5', label: 'Countries Served', sublabel: 'Uganda, Kenya, Rwanda, South Sudan, DRC', color: 'red' }];


const features = [
{
  icon: 'CheckBadgeIcon',
  title: 'Genuine & Quality Aftermarket',
  description: 'We stock a carefully curated mix of OEM-equivalent and quality aftermarket parts that meet European standards.',
  color: 'text-brand-red',
  bg: 'bg-red-50'
},
{
  icon: 'TruckIcon',
  title: 'Fast East Africa Delivery',
  description: 'Strategic location in Kampala enables rapid dispatch to Uganda, Kenya, Rwanda, South Sudan and DRC.',
  color: 'text-brand-blue',
  bg: 'bg-blue-50'
},
{
  icon: 'PhoneArrowUpRightIcon',
  title: 'Expert Technical Support',
  description: 'Our parts specialists have deep knowledge of European truck systems and will help you find the right component.',
  color: 'text-brand-yellow-dark',
  bg: 'bg-yellow-50'
},
{
  icon: 'CurrencyDollarIcon',
  title: 'Competitive Pricing',
  description: 'Bulk import capabilities allow us to offer the best prices on heavy truck spare parts in the region.',
  color: 'text-brand-red',
  bg: 'bg-red-50'
}];


export default function WhyChooseUs() {
  return (
    <section id="why-us" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-16 reveal-hidden" data-reveal>
          <p className="text-brand-yellow-dark text-xs font-heading font-black uppercase tracking-widest mb-3">
            Why Baninvest
          </p>
          <h2 className="font-heading font-black text-brand-dark text-3xl md:text-5xl tracking-tight mb-4">
            East Africa&apos;s Most Trusted{' '}
            <span className="text-gradient-red-blue">Parts Supplier</span>
          </h2>
          <p className="text-brand-muted font-body max-w-2xl mx-auto text-base md:text-lg">
            Uganda accounts for 37% of regional auto parts import shipments. Baninvest is at the heart of this supply chain.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 mb-20">
          {stats.map((stat, i) =>
          <div
            key={stat.value}
            className={`stat-card ${stat.color} reveal-hidden stagger-${i + 1}`}
            data-reveal>
            
              <p
              className="font-heading font-black mb-2"
              style={{
                fontSize: 'clamp(28px, 4vw, 52px)',
                color: stat.color === 'red' ? '#C41E3A' : stat.color === 'blue' ? '#1B3A8C' : '#D4891A'
              }}>
              
                {stat.value}
              </p>
              <p className="font-heading font-bold text-brand-dark text-sm mb-1">{stat.label}</p>
              <p className="font-body text-brand-muted text-xs leading-snug">{stat.sublabel}</p>
            </div>
          )}
        </div>

        {/* Asymmetric Layout: Image + Features */}
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-10 items-center">
          {/* Image side — 2 cols */}
          <div className="lg:col-span-2 reveal-hidden-scale" data-reveal>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl aspect-[3/4]">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1de7baf5e-1772024283895.png"
                  alt="Baninvest auto spare parts warehouse in Kampala Uganda with truck parts on shelves"
                  fill
                  className="object-cover" />
                
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/60 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass-dark rounded-2xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-brand-yellow flex items-center justify-center flex-shrink-0">
                        <Icon name="MapPinIcon" size={18} className="text-brand-dark" variant="solid" />
                      </div>
                      <div>
                        <p className="text-white font-heading font-bold text-sm">Mengo Hill Road</p>
                        <p className="text-white/60 font-body text-xs">Kampala, Uganda · PO BOX 133230</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating accent card */}
              <div
                className="absolute -top-5 -right-5 glass-card rounded-2xl px-5 py-4 shadow-xl animate-float-medium"
                aria-hidden="true">
                
                <p className="font-heading font-black text-brand-red text-2xl">37%</p>
                <p className="font-body text-brand-muted text-xs">of EA parts imports<br />via Uganda</p>
              </div>
            </div>
          </div>

          {/* Features side — 3 cols */}
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4 sm:gap-5">
            {features.map((feat, i) =>
            <div
              key={feat.title}
              className={`rounded-2xl p-5 sm:p-6 border border-brand-border hover:border-brand-red/30 transition-all hover:shadow-card-hover reveal-hidden stagger-${i + 1}`}
              data-reveal>
              
                <div className={`w-12 h-12 rounded-xl ${feat.bg} flex items-center justify-center mb-4`}>
                  <Icon name={feat.icon as 'CheckBadgeIcon'} size={22} className={feat.color} />
                </div>
                <h3 className="font-heading font-bold text-brand-dark text-base mb-2">{feat.title}</h3>
                <p className="font-body text-brand-muted text-sm leading-relaxed">{feat.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}